import React, { useEffect, useMemo, useRef, useState } from "react";

type NegotiateResponse = {
  url: string;
  tenantGroup: string;
  userGroup?: string;
  expiresIn?: number;
};

type HeartbeatPreview = {
  mime: string; // "image/jpeg" or "image/png"
  w: number;
  h: number;
  b64: string;
};

type PreviewCache = {
  preview: HeartbeatPreview;
  seenAt: number; // Date.now() when received
};

type HeartbeatMsg = {
  type: "headset.heartbeat";
  headsetId: string;
  ts?: number;
  meta?: any;
  preview?: HeartbeatPreview;
};

type VrStateMsg = {
  type: "vr.state";
  headsetId: string;
  scene?: number; // 3,4,5
  step?: number | null; // 1..13 when scene 4
  settingsOpen?: boolean;
  subtitlesEnabled?: boolean;
  stimulus?: string;
  ts?: number;
};

type Command = "start" | "skip" | "reset" | "swap_stimulus" | "toggle_subtitles";

type KeycloakLike = {
  token?: string;
  updateToken: (minValidity: number) => Promise<boolean>;
};

type Props = {
  apiBaseUrl: string;
  kc: KeycloakLike;
  enabled?: boolean;
};

const WS_SUBPROTOCOL = "json.webpubsub.azure.v1";

function mapWebState(s?: VrStateMsg): string {
  if (!s) return "-";

  if (s.settingsOpen) return "Scene05 - Settingspanel open";

  if (s.scene === 3) return "Scene03 - Idle";
  if (s.scene === 5) return "Scene05 - Settingspanel open";

  if (s.scene === 4) {
    const step = s.step ?? 0;
    if (step >= 1 && step <= 2) return "Scene04 - Intro";
    if (step === 3) return "Scene04 - Press start";
    if (step >= 4 && step <= 6) return "Scene04 - Measurement Intro";
    if (step === 7) return "Scene04 - Measurement";
    if (step === 8) return "Scene04 - Measurement End";
    if (step >= 9 && step <= 13) return "Scene04 - End";
    return "Scene04 - Intro";
  }

  return "-";
}

export default function HeadsetControlBubble({ apiBaseUrl, kc, enabled = true }: Props) {
  const [open, setOpen] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);

  const [lastSeenByHeadset, setLastSeenByHeadset] = useState<Record<string, number>>({});
  const [metaByHeadset, setMetaByHeadset] = useState<Record<string, any>>({});
  const [vrStateByHeadset, setVrStateByHeadset] = useState<Record<string, VrStateMsg>>({});

  const [selectedHeadsetId, setSelectedHeadsetId] = useState<string | null>(null);

  const [previewByHeadset, setPreviewByHeadset] = useState<Record<string, PreviewCache>>({});

  const wsRef = useRef<WebSocket | null>(null);
  const tenantGroupRef = useRef<string | null>(null);

  // Presence timeout: 10s since last heartbeat
  const onlineThresholdMs = 10_000;

  // Global ticker so bubble flips grey even without new messages
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const sortedHeadsets = useMemo(() => {
    const ids = Object.keys(lastSeenByHeadset);
    ids.sort((a, b) => (lastSeenByHeadset[b] ?? 0) - (lastSeenByHeadset[a] ?? 0));
    return ids;
  }, [lastSeenByHeadset]);

  useEffect(() => {
    if (!sortedHeadsets.length) return;
    if (selectedHeadsetId && sortedHeadsets.includes(selectedHeadsetId)) return;
    setSelectedHeadsetId(sortedHeadsets[0]);
  }, [sortedHeadsets, selectedHeadsetId]);

  const anyOnline = useMemo(() => {
    const t = Date.now();
    return Object.values(lastSeenByHeadset).some((seen) => t - seen <= onlineThresholdMs);
  }, [lastSeenByHeadset, onlineThresholdMs, tick]);

  const selectedOnline = useMemo(() => {
    if (!selectedHeadsetId) return false;
    const last = lastSeenByHeadset[selectedHeadsetId];
    if (!last) return false;
    return Date.now() - last <= onlineThresholdMs;
  }, [selectedHeadsetId, lastSeenByHeadset, onlineThresholdMs, tick]);

  const selectedVr = selectedHeadsetId ? vrStateByHeadset[selectedHeadsetId] : undefined;
  const webStateLabel = mapWebState(selectedVr);

  // Escape closes overlay
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  async function ensureFreshToken(): Promise<string | null> {
    try {
      await kc.updateToken(60);
      const t = kc.token;
      return t && t.trim().length ? t : null;
    } catch {
      return null;
    }
  }

  //Add a cleanup effect (runs every tick) to drop stale images:
  useEffect(() => {
    const now = Date.now();
    setPreviewByHeadset((prev) => {
      let changed = false;
      const next: Record<string, PreviewCache> = { ...prev };
      for (const [id, v] of Object.entries(prev)) {
        if (now - v.seenAt > onlineThresholdMs) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [tick, onlineThresholdMs]);


  // Connect Web PubSub as operator
  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    async function connect() {
      const token = await ensureFreshToken();
      if (!token) return;

      try {
        const res = await fetch(`${apiBaseUrl}/realtime/negotiate`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ clientType: "operator" }),
        });

        if (!res.ok) return;

        const data: NegotiateResponse = await res.json();
        if (cancelled) return;

        tenantGroupRef.current = data.tenantGroup;

        const ws = new WebSocket(data.url, WS_SUBPROTOCOL);
        wsRef.current = ws;

        ws.onopen = () => {
          setWsConnected(true);
          if (tenantGroupRef.current) {
            ws.send(JSON.stringify({ type: "joinGroup", group: tenantGroupRef.current }));
          }
        };

        ws.onclose = () => {
          setWsConnected(false);
          wsRef.current = null;
        };

        ws.onerror = () => setWsConnected(false);

        ws.onmessage = (evt) => {
          try {
            const msg = JSON.parse(evt.data);
            const payload = msg?.type === "message" ? msg.data : msg;
            if (!payload?.type) return;

            if (payload.type === "headset.heartbeat") {
              const hb = payload as HeartbeatMsg;
              const id = (hb.headsetId ?? "").trim();
              if (!id) return;

              if (hb.preview?.b64 && hb.preview?.mime) {
                const p = hb.preview;
                // basic sanity: reject huge images
                if (p.b64.length < 200_000) {
                  const seenAt = Date.now();
                  setPreviewByHeadset((prev) => ({ ...prev, [id]: { preview: p, seenAt } }));
                }
              }

              setLastSeenByHeadset((prev) => ({ ...prev, [id]: Date.now() }));
              if (hb.meta !== undefined) {
                setMetaByHeadset((prev) => ({ ...prev, [id]: hb.meta }));
              }
              return;
            }

            if (payload.type === "vr.state") {
              const st = payload as VrStateMsg;
              const id = (st.headsetId ?? "").trim();
              if (!id) return;

              setVrStateByHeadset((prev) => ({ ...prev, [id]: st }));
              return;
            }
          } catch {
            // ignore
          }
        };
      } catch {
        // ignore
      }
    }

    connect();

    return () => {
      cancelled = true;
      try {
        wsRef.current?.close();
      } catch {
        // ignore
      }
      wsRef.current = null;
      tenantGroupRef.current = null;
    };
  }, [apiBaseUrl, enabled, kc]);

  async function sendCommand(cmd: Command) {
    const id = (selectedHeadsetId ?? "").trim();
    if (!id) return;

    const token = await ensureFreshToken();
    if (!token) return;

    await fetch(`${apiBaseUrl}/headsets/${encodeURIComponent(id)}/command`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ cmd }),
    });
  }

  const btnStyle: React.CSSProperties = {
    flex: 1,
    padding: "10px 10px",
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.18)",
    cursor: selectedHeadsetId ? "pointer" : "not-allowed",
    background: "white",
    fontWeight: 700,
  };

  if (!enabled) return null;
  const selectedPreview =  selectedHeadsetId && selectedOnline ? previewByHeadset[selectedHeadsetId]?.preview : undefined;
  return (
    <>
      {/* Floating bubble */}
      <button
        type="button"
        aria-label="Headset control"
        onClick={() => setOpen(true)}
        style={{
          position: "fixed",
          right: 18,
          bottom: 18,
          width: 56,
          height: 56,
          borderRadius: 999,
          border: "1px solid rgba(0,0,0,0.12)",
          boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
          background: anyOnline ? "#1fbf5b" : "#9aa0a6",
          cursor: "pointer",
          zIndex: 9999,
        }}
        title={anyOnline ? "Headset online" : "No headset online"}
      >
        <span style={{ color: "white", fontWeight: 700, fontSize: 16 }}>VR</span>
      </button>

      {/* Overlay */}
      {open && (
        <div
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 10000,
            display: "flex",
            justifyContent: "flex-end",
            padding: 18,
          }}
        >
          <div
            style={{
              width: 380,
              maxWidth: "92vw",
              background: "white",
              borderRadius: 16,
              boxShadow: "0 18px 60px rgba(0,0,0,0.28)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                padding: "12px 14px",
                borderBottom: "1px solid rgba(0,0,0,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div style={{ display: "flex", flexDirection: "column" }}>
                <div style={{ fontWeight: 700 }}>Headset Control</div>
                <div style={{ fontSize: 12, opacity: 0.75 }}>
                  {wsConnected ? "Realtime connected" : "Realtime disconnected"}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: 18,
                  cursor: "pointer",
                  lineHeight: 1,
                  padding: 6,
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 12 }}>
              {/* Headset selector */}
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.75 }}>Headset</div>
                <select
                  value={selectedHeadsetId ?? ""}
                  onChange={(e) => setSelectedHeadsetId((e.target.value || "").trim() || null)}
                  style={{
                    width: "100%",
                    padding: "10px 10px",
                    borderRadius: 10,
                    border: "1px solid rgba(0,0,0,0.18)",
                    outline: "none",
                  }}
                >
                  {!sortedHeadsets.length && <option value="">No heartbeats yet</option>}
                  {sortedHeadsets.map((id) => {
                    const online = Date.now() - (lastSeenByHeadset[id] ?? 0) <= onlineThresholdMs;
                    return (
                      <option key={id} value={id}>
                        {online ? "● " : "○ "}
                        {id}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Status */}
              <div
                style={{
                  border: "1px solid rgba(0,0,0,0.10)",
                  borderRadius: 12,
                  padding: 12,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <Row label="Presence" value={selectedHeadsetId ? (selectedOnline ? "ONLINE" : "OFFLINE") : "-"} />
                <Row label="Web state" value={webStateLabel} />
                <Row label="Scene" value={selectedVr?.scene != null ? String(selectedVr.scene) : "-"} />
                <Row label="Step" value={selectedVr?.step != null ? String(selectedVr.step) : "-"} />
                <Row label="Stimulus" value={selectedVr?.stimulus ?? "-"} />
                <Row
                  label="Subtitles"
                  value={
                    selectedVr?.subtitlesEnabled == null ? "-" : selectedVr.subtitlesEnabled ? "ON" : "OFF"
                  }
                />

                {metaByHeadset[selectedHeadsetId ?? ""] != null && (
                  <div style={{ fontSize: 12, opacity: 0.8, marginTop: 6 }}>
                    {typeof metaByHeadset[selectedHeadsetId ?? ""] === "string"
                      ? metaByHeadset[selectedHeadsetId ?? ""]
                      : JSON.stringify(metaByHeadset[selectedHeadsetId ?? ""])}
                  </div>
                )}
              </div>

              {/* Controls */}
              <div style={{ display: "flex", gap: 10 }}>
                <button style={btnStyle} disabled={!selectedHeadsetId} onClick={() => sendCommand("start")}>
                  Start
                </button>
                <button style={btnStyle} disabled={!selectedHeadsetId} onClick={() => sendCommand("skip")}>
                  Skip
                </button>
                <button style={btnStyle} disabled={!selectedHeadsetId} onClick={() => sendCommand("reset")}>
                  Reset
                </button>
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  style={btnStyle}
                  disabled={!selectedHeadsetId}
                  onClick={() => sendCommand("swap_stimulus")}
                >
                  Swap stimulus
                </button>
                <button
                  style={btnStyle}
                  disabled={!selectedHeadsetId}
                  onClick={() => sendCommand("toggle_subtitles")}
                >
                  Toggle subtitles
                </button>
              </div>

              <div style={{ fontSize: 12, opacity: 0.7 }}>Presence timeout: 10s since last heartbeat.</div>

              {selectedPreview ? (
                <div
                  style={{
                    border: "1px solid rgba(0,0,0,0.10)",
                    borderRadius: 12,
                    overflow: "hidden",
                    background: "black",
                  }}
                >
                  <div style={{ padding: "10px 12px", background: "white" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.75 }}>Live preview</div>
                  </div>

                  <div
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      background: "black",
                    }}
                  >
                    <img
                      alt="Headset preview"
                      src={`data:${selectedPreview.mime};base64,${selectedPreview.b64}`}
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                        objectFit: "contain",
                      }}
                    />
                  </div>
                </div>
              ) : null}

            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.75 }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 700, textAlign: "right" }}>{value}</div>
    </div>
  );
}